import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject, Subscription } from 'rxjs';
import { AccessLogsService } from './access-logs.service';
import { InlineResponse200Results } from '../../api/generated/model/inlineResponse200Results';
import { InlineResponse200 } from '../../api/generated/model/inlineResponse200';
import {
  DataChangedService,
  HEADLINE_TYPE,
} from '../../data-changed.service';

@Component({
    selector: 'app-access-logs',
    templateUrl: './access-logs.component.html',
    styleUrls: ['./access-logs.component.scss'],
    standalone: false
})
export class AccessLogsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<InlineResponse200Results>([]);
  loading = false;
  error: string | null = null;

  // Pagination state
  totalItems = 0;
  currentPage = 0;
  pageSize = 50;

  // Make Math available in template
  Math = Math;

  // Table columns
  displayedColumns: string[] = ['access_datetime', 'user', 'report', 'project'];

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];

  constructor(
    private accessLogsService: AccessLogsService,
    private dataChangedService: DataChangedService
  ) {}

  ngOnInit(): void {
    this.dataChangedService.setHeadline({
      headline: 'Zugriffsprotokolle',
      breadcrumbs: [
        { label: 'Einstellungen', route: '/settings' },
        { label: 'Zugriffsprotokolle' },
      ],
      image: null,
      type: HEADLINE_TYPE.GENERIC,
    });
    this.subscribeToServiceState();
    this.loadAccessLogs();
  }

  ngAfterViewInit() {
    // Configure the paginator with our custom pagination
    // Use setTimeout to ensure the paginator is fully initialized
    setTimeout(() => {
      if (this.paginator) {
        this.paginator.pageIndex = this.currentPage;
        this.paginator.pageSize = this.pageSize;
        this.paginator.length = this.totalItems;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private subscribeToServiceState(): void {
    const loadingSub = this.accessLogsService.loading$.subscribe(
      (loading: boolean) => {
        this.loading = loading;
      }
    );

    const errorSub = this.accessLogsService.error$.subscribe(
      (error: string | null) => {
        this.error = error;
      }
    );

    this.subscriptions.push(loadingSub, errorSub);
  }

  loadAccessLogs(): void {
    const page = this.currentPage + 1; // API uses 1-based indexing
    this.accessLogsService.loadAccessLogs(page, this.pageSize).subscribe({
      next: (response: InlineResponse200) => {
        this.dataSource.data = response.results || [];
        this.totalItems = response.count || 0;

        // Update paginator state after loading data
        if (this.paginator) {
          this.paginator.length = this.totalItems;
          this.paginator.pageIndex = this.currentPage;
          this.paginator.pageSize = this.pageSize;
        }
      },
      error: (error) => {
        console.error('Error loading access logs:', error);
        // Error is handled by the service
      },
    });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadAccessLogs();
  }

  goToFirstPage(): void {
    this.currentPage = 0;
    this.loadAccessLogs();
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadAccessLogs();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.getTotalPages() - 1) {
      this.currentPage++;
      this.loadAccessLogs();
    }
  }

  goToLastPage(): void {
    this.currentPage = this.getTotalPages() - 1;
    this.loadAccessLogs();
  }

  getTotalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  refreshLogs(): void {
    this.currentPage = 0; // Reset to first page
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadAccessLogs();
  }

  exportLogs(): void {
    this.accessLogsService.exportAccessLogs().subscribe({
      next: () => {
        // Export completed successfully
      },
      error: () => {
        // Error is handled by the service
      },
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;

    // For now, we'll keep client-side filtering on the current page
    // In a future implementation, this could be extended to support server-side filtering
    this.dataSource.filter = filterValue.trim();
  }

  getDisplayName(log: InlineResponse200Results): string {
    return this.accessLogsService.getDisplayName(log);
  }

  get filteredItems(): number {
    return this.dataSource.data.length;
  }
}
